'use client';

import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import PostRouter from '../components/PostRouter';
import UserPrincipalSidebar from '../components/UserPrincipalSidebar';
import AtletaTorneiosTab from '../components/AtletaTorneiosTab';
import AtletaSeguindoTab from '../components/AtletaSeguindoTab';
import AtletaJogosTab from '../components/AtletaJogosTab';
import Load from '../components/Load';
import ProfilecardThree from '../components/ProfilecardThree';
import Createpost from '../components/Createpost';
import UserTabs, { UserTabId } from '../components/UserTabs';
import Pagination from '../components/Pagination';
import { ATLETA_POR_ID, AtletaData, AtletaVars } from '../services/atletaService';
import {
    POSTS_POR_ATLETA,
    PostsPorAtletaData,
    PostsPorAtletaVars,
    POST_POR_ID,
    PostPorIdData,
    PostPorIdVars,
    Post,
} from '../services/postService';
import {
    INSCRICOES_TORNEIO_POR_ATLETA,
    InscricoesTorneioPorAtletaData,
    InscricoesTorneioPorAtletaVars,
} from '../services/inscricaoTorneioService';
import {
    SEGUIDOS_POR_ATLETA,
    SeguidosPorAtletaData,
    SeguidosPorAtletaVars,
} from '../services/seguidorService';
import { useAuth } from '../context/AuthContext';


const Userpage: React.FC = () => {
    const { id, postId: postIdPath } = useParams<{ id: string; postId?: string }>();
    const [searchParams] = useSearchParams();
    const postIdQuery = searchParams.get('postId');
    const postId = postIdPath || postIdQuery || '';
    const [page, setPage] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const usuarioLogado = (auth?.user as any) || JSON.parse(localStorage.getItem('user') || '{}');
    const isAtletaLogado = usuarioLogado?.atleta?.id === id;

    const hashToTab = useCallback((hash: string): UserTabId => {
        if (hash === '#seguindo') return 'seguindo';
        if (hash === '#torneios') return 'torneios';
        if (hash === '#jogos') return 'jogos';
        return 'principal';
    }, []);

    const [activeTab, setActiveTabState] = useState<UserTabId>(() => hashToTab(location.hash));

    useEffect(() => {
        setActiveTabState(hashToTab(location.hash));
    }, [location.hash, hashToTab]);

    const onTabChange = useCallback((tab: UserTabId) => {
        setActiveTabState(tab);
        if (typeof window !== 'undefined') {
            if (tab === 'principal') {
                window.history.replaceState(null, '', location.pathname);
            } else {
                window.history.replaceState(null, '', `${location.pathname}#${tab}`);
            }
        }
    }, [location.pathname]);


    const { data, loading, error } = useQuery<AtletaData, AtletaVars>(ATLETA_POR_ID, {
        variables: { id: id || '' },
        skip: !id,
    });

    const {
        data: postData,
        loading: postLoading,
        error: postError,
    } = useQuery<PostPorIdData, PostPorIdVars>(POST_POR_ID, {
        variables: { id: postId },
        skip: !postId,
    });

    const {
        data: postsData,
        loading: postsLoading,
        error: postsError,
        fetchMore, refetch
    } = useQuery<PostsPorAtletaData, PostsPorAtletaVars>(POSTS_POR_ATLETA, {
        variables: { atletaId: id || '', page, size: 10 },
        skip: !id || !!postId,
    });

    const {
        data: torneiosData,
        loading: torneiosLoading,
    } = useQuery<InscricoesTorneioPorAtletaData, InscricoesTorneioPorAtletaVars>(
        INSCRICOES_TORNEIO_POR_ATLETA,
        { variables: { atletaId: id || '', page: 0, size: 4 }, skip: !id }
    );

    const {
        data: seguidosData,
        loading: seguidosLoading,
    } = useQuery<SeguidosPorAtletaData, SeguidosPorAtletaVars>(
        SEGUIDOS_POR_ATLETA,
        { variables: { atletaId: id || '', page: 0, size: 8 }, skip: !id }
    );

    const atleta = data?.atletaPorId;
    const torneios = torneiosData?.inscricoesTorneioPorAtletaId.items ?? [];
    const seguidos = seguidosData?.seguidosPorAtletaId.items ?? [];
    const posts = postsData?.postsPorAtleta.items ?? [];
    const totalPages = postsData?.postsPorAtleta.totalPages ?? 0;
    const singlePost = postData?.postPorId || null;

    const handlePostCreated = async () => {
        await refetch({ page: 0, size: (page + 1) * 10 });
        setPage(0);
    };

    return (
        <Fragment>
            <Header />


            <div className="main-content right-chat-active">
                <div className="middle-sidebar-bottom">
                    <div className="middle-sidebar-left pe-0">
                        <div className="row">
                            <div className="col-12">
                                {!id && (
                                    <div className="card shadow-xss rounded-xxl border-0 p-4 text-center">
                                        <i className="feather-user font-xxl text-grey-300 d-block mb-2"></i>
                                        <p className="text-grey-500 font-xss">Perfil de atleta não encontrado para este usuário.</p>
                                    </div>
                                )}
                                {loading && <p>Carregando dados...</p>}
                                {error && <p>Erro ao carregar atleta.</p>}
                            </div>
                            {atleta && (
                                <>
                                    <div className="col-xl-12 mb-3">
                                        <ProfilecardThree
                                            usuario={atleta.usuario}
                                            atletaId={id ? id : ''}
                                            isAtletaLogado={isAtletaLogado}
                                            isAdmin={usuarioLogado?.role === 'ADMIN'}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="col-xl-12">
                                <UserTabs
                                    activeTab={activeTab}
                                    onTabChange={onTabChange}
                                    torneiosContent={<AtletaTorneiosTab atletaId={id || ''} />}
                                    seguindoContent={<AtletaSeguindoTab atletaId={id || ''} />}
                                    jogosContent={<AtletaJogosTab atletaId={id || ''} />}
                                    principalContent={
                                        <div className="row">
                                            <div className="col-xl-4 col-xxl-3 col-lg-4 pe-0">
                                                <UserPrincipalSidebar
                                                    atleta={atleta}
                                                    atletaLoading={loading}
                                                    torneios={torneios}
                                                    torneiosLoading={torneiosLoading}
                                                    seguidos={seguidos}
                                                    seguidosLoading={seguidosLoading}
                                                    onVerTodosTorneios={() => onTabChange('torneios')}
                                                    onVerTodosSeguindo={() => onTabChange('seguindo')}
                                                />
                                            </div>
                                            <div className="col-xl-8 col-xxl-9 col-lg-8">
                                                {isAtletaLogado && <Createpost autorAtleta={usuarioLogado?.atleta} onPostCreated={handlePostCreated} />}
                                                {postId ? (
                                                    postLoading ? (
                                                        <Load />
                                                    ) : postError || !singlePost ? (
                                                        <p>Postagem não encontrada</p>
                                                    ) : (
                                                        <PostRouter
                                                            key={singlePost.id}
                                                            post={singlePost}
                                                        />
                                                    )
                                                ) : (
                                                    <>
                                                        {postsLoading && <Load />}
                                                        {postsError && (
                                                            <div className="alert alert-danger" role="alert">
                                                                Erro ao carregar posts.
                                                            </div>
                                                        )}
                                                        {!postsLoading && posts.length === 0 && (
                                                            <p className="text-center">Nenhum post encontrado.</p>
                                                        )}
                                                        {posts.map((post) => (
                                                            <PostRouter
                                                                key={post.id}
                                                                post={post}
                                                                atletaId={post.autorAtleta?.id}
                                                            />
                                                        ))}
                                                        <div className="mt-3">
                                                            <Pagination
                                                                currentPage={page}
                                                                totalPages={totalPages}
                                                                onPageChange={setPage}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>


        </Fragment>
    )
}

export default Userpage
